import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { dateDistanceToNowAsText } from '~/shared/date/date';

import { InviteTeamMemberDialog } from '../../company-settings/tabs/organization-tab/users-and-roles/InviteTeamMemberDialog';
import { useInviteTeamMember } from '../api/use-invite-team-member';
import { type JobItemDetails, useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { FormattedName } from '../components/FormattedName';
import { GenericAssignReviewerBadge } from '../components/ReviewerComponents';
import { ProfileStatus } from '../components/Status';
import { AuditTable, AuditTableSkeleton } from './AuditTable';

const MetadataSection = styled.div(({ theme }) => ({
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    flexWrap: 'wrap',
    gap: theme.spacing.xxl,
    justifyContent: 'flex-start',
}));

const Section = styled.div(({ theme }) => ({
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: theme.spacing.s,
    justifyContent: 'flex-start',
    position: 'relative',
}));

const MetadataLabel = styled.div(({ theme }) => ({
    color: theme.colors.gray[700],
    flexShrink: 0,
    position: 'relative',
    textAlign: 'left',
    ...theme.typography.body.S.medium,
}));

const MetadataValue = styled.div({
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    justifyContent: 'flex-start',
    padding: 0,
    position: 'relative',
});

const ValueText = styled.div(({ theme }) => ({
    color: theme.colors.black[900],
    display: 'flex',
    flexBasis: 0,
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: '1px',
    minWidth: '1px',
    position: 'relative',
    width: '100%',
    ...theme.typography.body.S.medium,
}));

const UpdatedAtText: FunctionComponent<{ readonly profile: JobItemDetails['jobProfile'] }> = ({ profile }) => {
    const { applicationState } = useApplicationContext();
    const { language } = applicationState;
    const updatedAtText = dateDistanceToNowAsText(Date.now(), language.date.locale, profile?.updatedAt ?? '').unwrapOr(
        '-',
    );

    return updatedAtText;
};

const LegacyMetadataContent: FunctionComponent<{
    readonly isArchived?: boolean;
    readonly item: JobItemDetails;
    readonly version: string | undefined;
}> = ({ isArchived, item, version }) => {
    const translate = useTranslate();
    const { availableMethods, closeInviteDialog, handleInvite, isInviteDialogOpen, openInviteDialog } =
        useInviteTeamMember({
            architectureId: item.architectureId,
            itemId: item.id,
            itemType: item.type,
        });
    const {
        data: versions,
        error,
        isLoading: isLoadingVersions,
    } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });

    const currentVersion = versions?.find((v) => v.version === Number(version));

    if (isLoadingVersions || error !== null || is.nullOrUndefined(currentVersion)) {
        return null;
    }

    return (
        <Fragment>
            <MetadataSection>
                <ProfileStatus
                    isActive={false}
                    isApproved={currentVersion.reviewedAt !== null}
                    profile={item}
                    published={currentVersion.publishedAt !== null}
                    statistics={{
                        approvedLevels: 0,
                        approvedRoles: 0,
                        clustersCount: 0,
                        expected: 0,
                        familiesCount: 0,
                        levelsCount: 0,
                        profiles: 0,
                        publishedLevels: 0,
                        publishedRoles: 0,
                        reviewed: 0,
                        reviewerLevels: 0,
                        reviewerRoles: 0,
                        reviewers: 0,
                        rolesCount: 0,
                    }}
                />
                <Section>
                    <MetadataLabel>{translate('pages.job-architecture-v2.job-profile-created-by')}</MetadataLabel>
                    <MetadataValue>
                        <ValueText>
                            <FormattedName
                                defaultText={translate('pages.job-architecture-v2.job-profile-unknown-user')}
                                reviewer={item.creator}
                            />
                        </ValueText>
                    </MetadataValue>
                </Section>
                <Section>
                    <MetadataLabel>{translate('pages.job-architecture-v2.job-profile-reviewer-label')}</MetadataLabel>
                    <MetadataValue>
                        <ValueText>
                            <GenericAssignReviewerBadge
                                disabled={isArchived}
                                isSelected={false}
                                item={item}
                                itemType={item.type}
                                jobArchitectureId={item.architectureId}
                                jobClusterId={item.parentId}
                                onlyAssignReviewer={true}
                                showOnlyWhenUnassigned={false}
                                onInvite={openInviteDialog}
                            />
                        </ValueText>
                    </MetadataValue>
                </Section>
                <Section>
                    <MetadataLabel>{translate('pages.job-architecture-v2.job-profile-last-modified')}</MetadataLabel>
                    <ValueText>
                        <UpdatedAtText profile={item.jobProfile} />
                    </ValueText>
                </Section>
            </MetadataSection>
            <InviteTeamMemberDialog
                availableMethods={availableMethods}
                open={isInviteDialogOpen}
                title={translate('pages.company-settings.users-and-roles.invite-dialog.assign-review-title')}
                onClose={closeInviteDialog}
                onInvite={handleInvite}
            />
        </Fragment>
    );
};

const AuditTableMetadataContent: FunctionComponent<{
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly item: JobItemDetails;
    readonly version: string | undefined;
}> = ({ architectureId, isArchived, item, version }) => {
    const translate = useTranslate();
    const { availableMethods, closeInviteDialog, handleInvite, isInviteDialogOpen, openInviteDialog } =
        useInviteTeamMember({
            architectureId: item.architectureId,
            itemId: item.id,
            itemType: item.type,
        });
    const {
        data: versions,
        error,
        isLoading: isLoadingVersions,
    } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });

    const currentVersion = versions?.find((v) => v.version === Number(version)) ?? versions?.[0];

    if (isLoadingVersions || error !== null || is.nullOrUndefined(currentVersion)) {
        return null;
    }

    return (
        <Fragment>
            <AuditTable
                architectureId={architectureId}
                currentVersion={currentVersion}
                isCollapsible={true}
                isArchived={isArchived}
                item={item}
                onInviteReviewer={openInviteDialog}
            />
            <InviteTeamMemberDialog
                availableMethods={availableMethods}
                open={isInviteDialogOpen}
                title={translate('pages.company-settings.users-and-roles.invite-dialog.assign-review-title')}
                onClose={closeInviteDialog}
                onInvite={handleInvite}
            />
        </Fragment>
    );
};

export const SidepanelMetadata: FunctionComponent<{
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly itemId: string;
    readonly itemType?: 'job-level' | 'job-role';
    readonly jobFamilyId?: string;
    readonly version: string | undefined;
}> = ({ architectureId, isArchived, itemId, itemType = 'job-role', version }) => {
    const { applicationState } = useApplicationContext();
    const { data: item, error, isLoading } = useJobItemDetails({ architectureId, itemId, itemType, version });
    const translate = useTranslate();
    const isAuditTableEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('review-status-table');

    if (isLoading) {
        if (isAuditTableEnabled) {
            return <AuditTableSkeleton />;
        }
        return <MetadataSection>{translate('pages.job-architecture-v2.job-profile-loading')}</MetadataSection>;
    }
    if (!is.nullOrUndefined(error)) {
        if (isAuditTableEnabled) {
            return (
                <div className="text-sm text-gray-700">
                    {translate('pages.job-architecture-v2.job-profile-error-prefix')}
                </div>
            );
        }
        return <MetadataSection>{translate('pages.job-architecture-v2.job-profile-error-prefix')}</MetadataSection>;
    }
    if (is.nullOrUndefined(item)) {
        if (isAuditTableEnabled) {
            return (
                <div className="text-sm text-gray-700">
                    {translate('pages.job-architecture-v2.job-profile-no-data-available', { itemType })}
                </div>
            );
        }
        return (
            <MetadataSection>
                {translate('pages.job-architecture-v2.job-profile-no-data-available', { itemType })}
            </MetadataSection>
        );
    }

    if (isAuditTableEnabled) {
        return (
            <AuditTableMetadataContent
                architectureId={architectureId}
                isArchived={isArchived}
                item={item}
                version={version}
            />
        );
    }

    return <LegacyMetadataContent isArchived={isArchived} item={item} version={version} />;
};
